/**
 * Iter 92 frontend — two focused pages (the admin KPI dashboard now lives in
 * admin/CustomerJourneyAnalytics.jsx as the "Business KPIs" tab):
 *   1. Public TrustPage         (/trust) — no auth
 *   2. NotificationPreferences  (per-user settings)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api, { formatApiError as fmt } from "../lib/api";
import { useToast } from "../lib/toast";
import Nav from "../components/Nav";


// ═══════════════════════════════════════════════════════════════════
// 1. Public Trust Page — /trust (no auth)
// ═══════════════════════════════════════════════════════════════════
export function TrustPage() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/public/trust-stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, []);

  // ── SEO / Snippet-friendly copy ────────────────────────────────
  // Round artists down to a 100-bucket for a snippet-friendly "1,000+ artists".
  const artistBucket = stats?.verified_artists
    ? `${Math.max(100, Math.floor(stats.verified_artists / 100) * 100).toLocaleString()}+`
    : "1,000+";
  const cityCount = stats?.cities_served || 50;
  const pageTitle = `Trusted by ${artistBucket} artists across ${cityCount} cities | BookTalent`;
  const pageDesc = `BookTalent — India's managed talent marketplace. ${artistBucket} verified artists and ${cityCount}+ cities served. Every metric refreshed live from the platform.`;
  const canonical = typeof window !== "undefined" ? `${window.location.origin}/trust` : "https://booktalent.in/trust";

  const orgLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "BookTalent",
    url: canonical.replace(/\/trust$/, ""),
    logo: `${canonical.replace(/\/trust$/, "")}/logo.png`,
    description: pageDesc,
    areaServed: { "@type": "Country", name: "India" },
  };
  const webPageLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: pageTitle,
    description: pageDesc,
    url: canonical,
    about: { "@type": "Organization", name: "BookTalent" },
  };

  return (
    <div>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large" />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="BookTalent" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonical} />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        {/* Structured data */}
        <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
        <script type="application/ld+json">{JSON.stringify(webPageLd)}</script>
      </Helmet>
      <Nav />
      <div className="pad-24" data-testid="trust-page" style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div className="text-center mb-24">
          <div className="text-muted fs-12" style={{ letterSpacing: ".18em", textTransform: "uppercase" }}>
            Live from BookTalent
          </div>
          <h1 className="font-serif" style={{ fontSize: 48, fontWeight: 700, color: "#D4AF37", marginTop: "var(--space-2)" }} data-testid="trust-title">
            Trusted by artists & organisers across India
          </h1>
          <div className="text-muted fs-16 mt-8" style={{ maxWidth: 640, margin: "var(--space-2) auto 0" }}>
            Every number below is refreshed straight from our platform — no marketing inflation.
          </div>
        </div>

        {!stats && <div className="text-center text-muted">Loading trust metrics…</div>}
        {stats && (
          <>
            <div className="grid grid-3 gap-16 mb-24">
              <div className="card card-pad text-center" data-testid="trust-artists">
                <div style={{ fontSize: 44 }}></div>
                <div className="font-serif fs-28 fw-700 text-gold mt-8">{stats.verified_artists?.toLocaleString()}</div>
                <div className="text-muted fs-12 mt-4">Verified Artists</div>
              </div>
              <div className="card card-pad text-center" data-testid="trust-events">
                <div style={{ fontSize: 44 }}></div>
                <div className="font-serif fs-28 fw-700 text-gold mt-8">{stats.completed_events?.toLocaleString()}</div>
                <div className="text-muted fs-12 mt-4">Events Delivered</div>
              </div>
              <div className="card card-pad text-center" data-testid="trust-cities">
                <div style={{ fontSize: 44 }}></div>
                <div className="font-serif fs-28 fw-700 text-gold mt-8">{stats.cities_served}</div>
                <div className="text-muted fs-12 mt-4">Cities Served</div>
              </div>
            </div>

            {stats.top_cities?.length > 0 && (
              <div className="card card-pad" data-testid="trust-cities-list">
                <h3 className="fw-700 mb-8">Cities we operate in</h3>
                <div className="flex gap-8" style={{ flexWrap: "wrap" }}>
                  {stats.top_cities.map((c) => (
                    <span key={c} className="pill pill-gold" style={{ fontSize: 12 }}>{c}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="text-center mt-24">
              <Link to="/search" className="btn btn-gold" data-testid="trust-cta">Explore artists →</Link>
              <Link to="/signup" className="btn btn-ghost" style={{ marginLeft: 8 }} data-testid="trust-cta-signup">List your talent →</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// 2. Notification Preferences
// ═══════════════════════════════════════════════════════════════════
const EVENT_LABELS = {
  "booking.confirmed":       "Booking Confirmed",
  "payment.received":        "Payment Received",
  "payout.released":         "Payout Released",
  "kyc.approved":            "KYC Approved",
  "kyc.rejected":            "KYC Rejected",
  "kyc.needs_resubmission":  "KYC Needs Resubmission",
  "event.reminder":          "Event Reminder",
  "payment.reminder":        "Payment Due Reminder",
  "marketing.digest":        "Marketing / Weekly Digest",
  "profile.nudge":           "Profile Nudges",
};

export function NotificationPreferences() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/user/notification-preferences");
      setData(r.data);
      setDrafts(r.data.preferences);
    } catch (e) { toast(fmt(e), "error"); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (event, channel) => {
    setDrafts((prev) => ({
      ...prev,
      [event]: { ...prev[event], [channel]: !prev[event][channel] },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      // Only send editable (non-force_on) events; strip force_on flag from payload.
      const payload = { preferences: {} };
      for (const [ev, chans] of Object.entries(drafts)) {
        if (chans.force_on) continue;
        payload.preferences[ev] = {
          email: chans.email, whatsapp: chans.whatsapp,
          in_app: chans.in_app, sms: chans.sms,
        };
      }
      await api.patch("/user/notification-preferences", payload);
      toast("Preferences saved", "success");
      load();
    } catch (e) { toast(fmt(e), "error"); }
    setSaving(false);
  };

  if (!data) return (
    <div>
      <Nav />
      <div className="pad-24 text-muted" data-testid="np-loading">Loading preferences…</div>
    </div>
  );

  return (
    <div>
      <Nav />
      <div className="pad-24" data-testid="notification-preferences" style={{ maxWidth: 800, margin: "0 auto" }}>
        <div className="mb-16">
          <h1 className="font-serif fs-28 fw-700 text-gold" data-testid="np-title">Notification Preferences</h1>
          <div className="text-muted fs-13 mt-4">
            Choose how you want to hear from us. Transactional notifications (locked events) can't be turned off — they're
            required for security and compliance.
          </div>
        </div>

        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Event</th>
                <th style={{ textAlign: "center", width: 80 }}>In-app</th>
                <th style={{ textAlign: "center", width: 80 }}>Email</th>
                <th style={{ textAlign: "center", width: 100 }}>WhatsApp</th>
                <th style={{ textAlign: "center", width: 80 }}>SMS</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(drafts).map(([ev, chans]) => (
                <tr key={ev} data-testid={`np-row-${ev.replace(".", "-")}`}>
                  <td>
                    <div className="fw-700 fs-13">{EVENT_LABELS[ev] || ev}</div>
                    {chans.force_on && (
                      <div className="text-muted fs-11" style={{ marginTop: "var(--space-1)" }}> Required · cannot be muted</div>
                    )}
                  </td>
                  {["in_app", "email", "whatsapp", "sms"].map((ch) => (
                    <td key={ch} style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={!!chans[ch]}
                        disabled={chans.force_on}
                        onChange={() => toggle(ev, ch)}
                        data-testid={`np-toggle-${ev.replace(".", "-")}-${ch}`}
                        style={{ transform: "scale(1.2)", cursor: chans.force_on ? "not-allowed" : "pointer" }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex gap-8 mt-16">
          <button className="btn btn-gold" onClick={save} disabled={saving} data-testid="np-save">
            {saving ? "Saving…" : "Save Preferences"}
          </button>
          <button className="btn btn-ghost" onClick={load}>Reset</button>
        </div>
      </div>
    </div>
  );
}
